import React, { useState, useEffect } from "react";

const MARINE_STANDARDS = {
  temperature: {
    ranges: [
      { min: -10, max: 50,  unit: "°C",   name: "Engine Temp"   },
      { min: 0,   max: 100, unit: "°C",   name: "Water Temp"    },
      { min: -20, max: 80,  unit: "°C",   name: "Ambient Temp"  },
    ],
    colors:     { normal: "#0C9377", warning: "#f59e0b", critical: "#ef4444" },
    thresholds: { warning: 70, critical: 85 },
  },
  speed: {
    ranges: [
      { min: 0, max: 30,   unit: "knots", name: "Obs Speed"    },
      { min: 0, max: 30,   unit: "knots", name: "Log Speed"    },
      { min: 0, max: 3600, unit: "rpm",   name: "Engine RPM"   },
      { min: 0, max: 100,  unit: "kW",    name: "ME Power"     },
      { min: 0, max: 36,   unit: "Hrs",   name: "Running Hrs"  },
    ],
    colors:     { normal: "#0C9377", warning: "#f59e0b", critical: "#ef4444" },
    thresholds: { warning: 80, critical: 95 },
  },
  pressure: {
    ranges: [
      { min: 0, max: 10, unit: "bar", name: "Oil Pressure"  },
      { min: 0, max: 50, unit: "psi", name: "Air Pressure"  },
      { min: 0, max: 5,  unit: "bar", name: "Fuel Pressure" },
    ],
    colors:     { normal: "#0C9377", warning: "#f59e0b", critical: "#ef4444" },
    thresholds: { warning: 75, critical: 90 },
  },
  percentage: {
    ranges: [
      { min: 0, max: 100, unit: "%", name: "Fuel Level"    },
      { min: 0, max: 100, unit: "%", name: "Battery Level" },
      { min: 0, max: 100, unit: "%", name: "ME Load"       },
    ],
    colors:     { normal: "#0C9377", warning: "#f59e0b", critical: "#ef4444" },
    thresholds: { warning: 20, critical: 10 },
  },
  consumption: {
    ranges: [{ min: 0, max: 100000, unit: "MT", name: "Consumption" }],
    colors:     { normal: "#0C9377", warning: "#f59e0b", critical: "#ef4444" },
    thresholds: { warning: 1, critical: 2 },
  },
  general: {
    ranges: [{ min: 0, max: 7000, unit: "", name: "" }],
    colors:     { normal: "#0C9377", warning: "#f59e0b", critical: "#ef4444" },
    thresholds: { warning: 75, critical: 90 },
  },
};

const MarineGaugeChart = ({
  value,
  type         = "percentage",
  rangeIndex   = 0,
  size         = 130,
  label,
  animationDuration = 800,
  showThresholds    = true,
}) => {
  const [animatedValue, setAnimatedValue] = useState(0);

  const config = MARINE_STANDARDS[type] ?? MARINE_STANDARDS.general;
  const range  = config.ranges[rangeIndex] ?? config.ranges[0];
  const { min, max, unit, name } = range;

  useEffect(() => {
    const endValue  = Math.min(Math.max(value, min), max);
    const startVal  = animatedValue;
    const startTime = Date.now();

    const tick = () => {
      const progress = Math.min((Date.now() - startTime) / animationDuration, 1);
      const ease     = 1 - Math.pow(1 - progress, 3);
      setAnimatedValue(startVal + (endValue - startVal) * ease);
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [value, min, max]);

  const percentage = Math.min(Math.max(((animatedValue - min) / (max - min)) * 100, 0), 100);

  const getColor = () => {
    const t = config.thresholds;
    if (type === "percentage") {
      if (percentage <= t.critical) return config.colors.critical;
      if (percentage <= t.warning)  return config.colors.warning;
    } else {
      if (percentage >= t.critical) return config.colors.critical;
      if (percentage >= t.warning)  return config.colors.warning;
    }
    return config.colors.normal;
  };

  const arcColor = getColor();

  // SVG geometry
  const cx        = size / 2;
  const cy        = size / 2;
  const r         = (size - 24) / 2;
  const circ      = Math.PI * r;
  const offset    = circ - (percentage / 100) * circ;

  // Needle
  const needleLen   = r * 0.72;
  const needleAngle = (180 - (percentage / 100) * 180) * (Math.PI / 180);
  const nx          = cx + needleLen * Math.cos(needleAngle);
  const ny          = cy - needleLen * Math.sin(needleAngle);

  // Threshold dots
  const thresholdDot = (pct, color) => {
    const a = (180 - (pct / 100) * 180) * (Math.PI / 180);
    return { x: cx + r * 0.88 * Math.cos(a), y: cy - r * 0.88 * Math.sin(a), color };
  };
  const warnDot  = thresholdDot(config.thresholds.warning,  "#f59e0b");
  const critDot  = thresholdDot(config.thresholds.critical, "#ef4444");

  // Tick marks
  const ticks = Array.from({ length: 13 }, (_, i) => {
    const a     = (180 - (i / 12) * 180) * (Math.PI / 180);
    const isMaj = i % 4 === 0;
    const r1    = r * 0.88;
    const r2    = r * (isMaj ? 0.76 : 0.82);
    return {
      x1: cx + r1 * Math.cos(a), y1: cy - r1 * Math.sin(a),
      x2: cx + r2 * Math.cos(a), y2: cy - r2 * Math.sin(a),
      isMaj,
    };
  });

  const svgH = size / 2 + 20;

  return (
    <div
      className="d-flex flex-column align-items-center"
      style={{
        background: "#ffffff",
        border: "1px solid #e8e8e8",
        borderRadius: 8,
        padding: "8px 6px 6px",
      }}
    >
      {/* Label */}
      <div
        className="text-center fw-semibold"
        style={{ fontSize: 11, color: "#444", marginBottom: 2, lineHeight: 1.2 }}
      >
        {label || name}
      </div>

      {/* SVG gauge */}
      <div style={{ position: "relative", width: size, height: svgH }}>
        <svg width={size} height={svgH}>
          <defs>
            <linearGradient id={`g-${type}-${rangeIndex}`} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={arcColor} stopOpacity="0.5" />
              <stop offset="100%" stopColor={arcColor} stopOpacity="1" />
            </linearGradient>
          </defs>

          {/* Background arc */}
          <path
            d={`M 12 ${cy} A ${r} ${r} 0 0 1 ${size - 12} ${cy}`}
            fill="none" stroke="#ebebeb" strokeWidth="9" strokeLinecap="round"
          />

          {/* Progress arc */}
          <path
            d={`M 12 ${cy} A ${r} ${r} 0 0 1 ${size - 12} ${cy}`}
            fill="none"
            stroke={`url(#g-${type}-${rangeIndex})`}
            strokeWidth="9"
            strokeLinecap="round"
            strokeDasharray={circ}
            strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 0.8s cubic-bezier(0.4,0,0.2,1)" }}
          />

          {/* Tick marks */}
          {ticks.map((t, i) => (
            <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
              stroke="#ddd" strokeWidth={t.isMaj ? 1.5 : 0.8} />
          ))}

          {/* Threshold dots */}
          {showThresholds && <>
            <circle cx={warnDot.x} cy={warnDot.y} r="2.5" fill="#f59e0b" opacity="0.8" />
            <circle cx={critDot.x} cy={critDot.y} r="2.5" fill="#ef4444" opacity="0.8" />
          </>}

          {/* Needle */}
          <line x1={cx} y1={cy} x2={nx} y2={ny}
            stroke={arcColor} strokeWidth="1.8" strokeLinecap="round" />
          <circle cx={cx} cy={cy} r="4" fill={arcColor} stroke="#fff" strokeWidth="1.5" />
          <circle cx={cx} cy={cy} r="2" fill="#fff" />
        </svg>

        {/* Value readout */}
        <div style={{
          position: "absolute", top: "72%", left: "50%",
          transform: "translate(-50%, -50%)", textAlign: "center", lineHeight: 1,
        }}>
          <div className="fw-bold" style={{ fontSize: 15, color: "#1a1a1a" }}>
            {animatedValue.toFixed(1)}
          </div>
          <div style={{ fontSize: 10, color: "#888", marginTop: 1 }}>{unit}</div>
        </div>
      </div>

      {/* Min / Max */}
      <div className="d-flex justify-content-between w-100 px-1"
        style={{ fontSize: 9, color: "#bbb", marginTop: 2 }}>
        <span>{min}{unit}</span>
        <span>{max}{unit}</span>
      </div>
    </div>
  );
};

export default MarineGaugeChart;